import React, { useState } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Button,
  Alert,
} from '@mui/material';
import {
  Save as SaveIcon,
  Print as PrintIcon,
  PersonAdd as RegistrationIcon,
  LocalHospital as ConsultationIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useFormData } from '../../contexts/FormDataContext';
import { SyncedConfidenceToggleProvider } from '../../contexts/ConfidenceToggleContext';
import Tab1_Registration from './tabs/Tab1_Registration';
import Tab2_Consultation from './tabs/Tab2_Consultation';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  consultationOnly?: boolean;
}

function TabPanel({ children, value, index, consultationOnly, ...other }: TabPanelProps) {
  if (value !== index) return null;

  return (
    <Box
      role="tabpanel"
      id={`opd-tabpanel-${index}`}
      aria-labelledby={`opd-tab-${index}`}
      sx={{ p: 1.5, pb: consultationOnly ? 1.5 : 8 }}
      {...other}
    >
      {children}
    </Box>
  );
}

function a11yProps(index: number) {
  return {
    id: `opd-tab-${index}`,
    'aria-controls': `opd-tabpanel-${index}`,
  };
}

interface NewOPDFormProps {
  sessionId: string;
  /** When true (e.g. from Patient Visit): hide Registration tab and tab bar; show only consultation form. Patient Visit acts as the consultation tab. */
  consultationOnly?: boolean;
}

export default function NewOPDForm({ sessionId, consultationOnly = false }: NewOPDFormProps) {
  return (
    <SyncedConfidenceToggleProvider>
      <NewOPDFormInner sessionId={sessionId} consultationOnly={consultationOnly} />
    </SyncedConfidenceToggleProvider>
  );
}

function NewOPDFormInner({ sessionId, consultationOnly = false }: NewOPDFormProps) {
  const [activeTab, setActiveTab] = useState(consultationOnly ? 1 : 0);
  const { isConnected, saveFormData, clearFormForTab } = useFormData();
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error' | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // const handleSave = () => {
  //   console.log('Save clicked for session:', sessionId);
  //   // TODO: Implement save logic
  // };

  const handleSave = async () => {
    setSaveStatus('saving');
    setSaveError(null);
    const result = await saveFormData();
    if (result.success) {
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
    } else {
      setSaveStatus('error');
      setSaveError(result.error || 'Save failed');
    }
  };
  const handleCancel = () => {
    if (consultationOnly) {
      clearFormForTab('Consultation');
    } else {
      clearFormForTab(activeTab === 0 ? 'Registration' : 'Consultation');
    }
  };
  const handlePrint = () => {
    window.print();
  };

  return (
    <Box sx={{ bgcolor: '#fafafa' }}>
      {/* Save feedback */}
      {saveStatus === 'success' && (
        <Alert severity="success" sx={{ m: 0.5, py: 0.25, fontSize: '0.75rem' }}>
          Form saved successfully
        </Alert>
      )}
      {saveStatus === 'error' && saveError && (
        <Alert severity="error" sx={{ m: 0.5, py: 0.25, fontSize: '0.75rem' }}>
          {saveError}
        </Alert>
      )}

      {/* Connection Warning */}
      {!isConnected && (
        <Alert severity="warning" sx={{ m: 0.5, py: 0.25, fontSize: '0.75rem' }}>
          Disconnected - Real-time updates unavailable
        </Alert>
      )}

      {/* Tab Navigation - hidden in Patient Visit; Registration tab hidden, Consultation content shown without tab label */}
      {!consultationOnly && (
        <Paper
          elevation={0}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            position: 'sticky',
            top: 0,
            zIndex: 9,
            bgcolor: 'white',
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="OPD Form Tabs"
            sx={{
              minHeight: 42,
              '& .MuiTab-root': { minHeight: 42, fontSize: '0.8rem', fontWeight: 500, textTransform: 'none' },
            }}
          >
            <Tab icon={<RegistrationIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Registration" {...a11yProps(0)} />
            <Tab icon={<ConsultationIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Consultation" {...a11yProps(1)} />
          </Tabs>
        </Paper>
      )}

      {/* Tab Content - when consultationOnly, only Consultation panel is shown (no tab bar) */}
      {!consultationOnly && (
        <TabPanel value={activeTab} index={0} consultationOnly={consultationOnly}>
          <Tab1_Registration sessionId={sessionId} />
        </TabPanel>
      )}
      <TabPanel value={activeTab} index={1} consultationOnly={consultationOnly}>
        <Tab2_Consultation sessionId={sessionId} />
        {consultationOnly && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, flexWrap: 'wrap', mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
            <Button
              variant="outlined"
              size="medium"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
              sx={{ borderColor: '#012676', color: '#012676', bgcolor: '#fff', '&:hover': { borderColor: '#011a5c', color: '#011a5c', bgcolor: 'rgba(1,38,118,0.04)' } }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size="medium"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saveStatus === 'saving'}
              sx={{ bgcolor: '#012676', color: '#fff', '&:hover': { bgcolor: '#011a5c' } }}
            >
              {saveStatus === 'saving' ? 'Saving…' : 'Save Patient Visit'}
            </Button>
            <Button
              variant="outlined"
              size="medium"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              sx={{ borderColor: '#012676', color: '#012676', bgcolor: '#fff', '&:hover': { borderColor: '#011a5c', color: '#011a5c', bgcolor: 'rgba(1,38,118,0.04)' } }}
            >
              Print
            </Button>
          </Box>
        )}
      </TabPanel>
    </Box>
  );
}

